// ─────────────────────────────────────────────────────────────────────────
// Clasificar compras (28-ago-2026, pedido Maxi — el Excel de la contadora).
//
// Cada factura de COMPRA se etiqueta con un toque: 🏭 Empresa · 🔧 Trabajo
// (con su OT) · 📌 Gasto fijo · 👤 Personal. Con "recordar para este
// proveedor" (marcado por defecto) el sistema APRENDE: la próxima compra del
// mismo RUT llega con sugerencia y el botón ⚡ clasifica en lote. "Ver
// detalle" muestra las líneas del XML DTE del archivo histórico (cobertura
// incompleta → aviso, nunca error). Pensada para Catalina: rapidez y celular.
// Se monta como pestaña "Clasificar" en Contabilidad (admin/accountant).
// ─────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState, useCallback } from "react";
import backend from "../../backend";
import useIsMobile from "../../hooks/useIsMobile";
import { formatPrice } from "../../utils";

const fmt = n => `${n < 0 ? "−" : ""}$${formatPrice(Math.round(Math.abs(n || 0)))}`;
const fmtD = d => d ? new Date(d).toLocaleDateString("es-CL", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const num = { fontVariantNumeric: "tabular-nums" };

const TIPOS = {
  empresa:    { emoji: "🏭", label: "Empresa",    color: "#3498db", bg: "#0d2035", border: "#1a4a6e" },
  trabajo:    { emoji: "🔧", label: "Trabajo",    color: "#f39c12", bg: "#2d2000", border: "#4a3500" },
  gasto_fijo: { emoji: "📌", label: "Gasto fijo", color: "#a78bfa", bg: "#221a33", border: "#3d2f5e" },
  personal:   { emoji: "👤", label: "Personal",   color: "#e74c3c", bg: "#2d1515", border: "#4a2020" },
};

const S = {
  card:  { background: "#232323", border: "1px solid #2e2e2e", borderRadius: 12, padding: "12px 14px" },
  input: {
    background: "#161616", border: "1px solid #333", borderRadius: 5,
    padding: "7px 10px", fontSize: 12, color: "#ddd",
    fontFamily: "Arial,sans-serif", boxSizing: "border-box",
  },
  chip: (on, color = "#e30613") => ({
    background: on ? "#3a1114" : "#1e1e1e",
    color: on ? "#ff8a8f" : "#999",
    border: `1px solid ${on ? color : "#333"}`,
    borderRadius: 6, padding: "5px 12px", fontSize: 11, cursor: "pointer",
  }),
};

const ClasificarCompras = () => {
  const isMobile = useIsMobile();

  const now = new Date();
  const [mes, setMes] = useState(String(now.getMonth() + 1).padStart(2, "0"));
  const [ano, setAno] = useState(String(now.getFullYear()));
  const periodo = `${ano}-${mes}`;

  const [compras, setCompras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vista, setVista] = useState("sin");        // sin | clasificadas
  const [msg, setMsg] = useState(null);             // resultado de ⚡ aplicar reglas
  const [aplicando, setAplicando] = useState(false);
  const [resumen, setResumen] = useState(null);

  const [savingId, setSavingId] = useState(null);
  const [fading, setFading] = useState(() => new Set());        // tarjetas desvaneciéndose
  const [recordar, setRecordar] = useState({});                 // id → bool (default true)
  const [otAbierto, setOtAbierto] = useState(null);             // id con selector de OT abierto
  const [otSel, setOtSel] = useState({});                       // id → workOrderId elegida
  const [workOrders, setWorkOrders] = useState(null);           // null = aún no cargadas
  const [detalles, setDetalles] = useState({});                 // id → {loading, lineas, sinXml, archivo}
  const [detalleAbierto, setDetalleAbierto] = useState({});     // id → bool
  const [editando, setEditando] = useState({});                 // id → bool (re-clasificar)

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // siempre TODAS: el contador y la vista "ya clasificadas" salen del mismo fetch
      const r = await backend.get("clasificacion/compras", { params: { periodo, estado: "todas" } });
      setCompras(r.data || []);
      setFading(new Set());
      setOtAbierto(null);
      setEditando({});
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    setLoading(false);
  }, [periodo]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    backend.get("clasificacion/compras/resumen", { params: { meses: 6 } }).then(r => setResumen(r.data || [])).catch(() => setResumen([]));
  }, [compras]);

  const cargarOts = async () => {
    if (workOrders !== null) return;
    try {
      const r = await backend.get("clasificacion/workorders");
      setWorkOrders(r.data || []);
    } catch { setWorkOrders([]); }
  };

  const clasificar = async (c, tipo, workOrderId = null) => {
    if (savingId) return;
    setSavingId(c._id);
    try {
      const r = await backend.patch(`clasificacion/compras/${c._id}`, {
        tipo,
        workOrder: workOrderId || null,
        recordarProveedor: recordar[c._id] !== false, // marcado por defecto
      });
      const nueva = r.data?.clasificacion || { tipo, workOrder: workOrderId };
      setOtAbierto(prev => (prev === c._id ? null : prev));
      setEditando(prev => ({ ...prev, [c._id]: false }));
      if (vista === "sin") {
        // la tarjeta se desvanece y sale de la lista
        setFading(prev => new Set(prev).add(c._id));
        setTimeout(() => {
          setCompras(prev => prev.map(x => x._id === c._id ? { ...x, clasificacion: nueva } : x));
          setFading(prev => { const n = new Set(prev); n.delete(c._id); return n; });
        }, 380);
      } else {
        setCompras(prev => prev.map(x => x._id === c._id ? { ...x, clasificacion: nueva } : x));
      }
    } catch (e) { alert(e?.response?.data?.error || e.message); }
    setSavingId(null);
  };

  const aplicarReglas = async () => {
    setAplicando(true);
    setMsg(null);
    try {
      const r = await backend.post(`clasificacion/compras/aplicar-reglas?periodo=${periodo}`);
      const { clasificadas, sinRegla, trabajoManual } = r.data || {};
      setMsg(`⚡ ${clasificadas || 0} compra${clasificadas === 1 ? "" : "s"} clasificada${clasificadas === 1 ? "" : "s"} por regla` +
        (sinRegla ? ` · ${sinRegla} sin regla conocida` : "") +
        (trabajoManual ? ` · ${trabajoManual} de proveedor "trabajo" quedan para elegir OT a mano` : ""));
      await cargar();
    } catch (e) { setMsg(`❌ ${e?.response?.data?.error || e.message}`); }
    setAplicando(false);
  };

  const verDetalle = async (c) => {
    const abierto = !!detalleAbierto[c._id];
    setDetalleAbierto(prev => ({ ...prev, [c._id]: !abierto }));
    if (abierto || detalles[c._id]) return;
    setDetalles(prev => ({ ...prev, [c._id]: { loading: true } }));
    try {
      const r = await backend.get(`clasificacion/compras/${c._id}/detalle`);
      setDetalles(prev => ({ ...prev, [c._id]: { loading: false, ...r.data } }));
    } catch {
      setDetalles(prev => ({ ...prev, [c._id]: { loading: false, lineas: [], sinXml: true } }));
    }
  };

  const sinClasificar = compras.filter(c => !c.clasificacion?.tipo);
  const clasificadas = compras.filter(c => !!c.clasificacion?.tipo);
  const visibles = vista === "sin" ? sinClasificar : clasificadas;

  // ── Bloque de botones de clasificación (pendientes y re-clasificar) ──
  // Función de render (no componente): así React NO remonta el bloque en cada
  // cambio de estado y el selector de OT no se cierra solo.
  const renderBotones = (c) => (
    <>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 6 }}>
        {Object.entries(TIPOS).map(([k, t]) => {
          const sugerido = c.sugerencia === k;
          return (
            <button key={k} type="button"
              disabled={savingId === c._id}
              className="t-02"
              onClick={() => {
                if (k === "trabajo") {
                  cargarOts();
                  setOtAbierto(prev => (prev === c._id ? null : c._id));
                } else {
                  clasificar(c, k);
                }
              }}
              style={{
                background: t.bg, color: t.color,
                border: `1px solid ${sugerido || (k === "trabajo" && otAbierto === c._id) ? t.color : t.border}`,
                boxShadow: sugerido ? `0 0 0 1px ${t.color}` : "none",
                borderRadius: 8, padding: "10px 6px", minHeight: 44,
                fontSize: 12, fontWeight: 700,
                cursor: savingId === c._id ? "wait" : "pointer",
              }}>
              {t.emoji} {t.label}{k === "trabajo" ? " ▾" : ""}
            </button>
          );
        })}
      </div>

      {/* selector de OT para 🔧 Trabajo */}
      {otAbierto === c._id && (
        <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
          <select
            value={otSel[c._id] || ""}
            onChange={e => setOtSel(prev => ({ ...prev, [c._id]: e.target.value }))}
            style={{ ...S.input, flex: "1 1 220px", minWidth: 0 }}>
            <option value="">{workOrders === null ? "Cargando OTs…" : "— ¿Para qué OT? (opcional) —"}</option>
            {(workOrders || []).map(wo => (
              <option key={wo._id} value={wo._id}>
                N°{wo.number} — {(wo.name || wo.client?.name || "—").slice(0, 40)} ({wo.status})
              </option>
            ))}
          </select>
          <button type="button"
            disabled={savingId === c._id}
            onClick={() => clasificar(c, "trabajo", otSel[c._id] || null)}
            className="t-02"
            style={{
              background: "#e30613", color: "#fff", border: "none", borderRadius: 8,
              padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer",
              minHeight: 40, flex: isMobile ? "1 1 100%" : "0 0 auto",
            }}>
            {savingId === c._id ? "Guardando…" : "✓ Clasificar como Trabajo"}
          </button>
        </div>
      )}

      <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, fontSize: 11, color: "#8a8a8a", cursor: "pointer", width: "fit-content" }}>
        <input
          type="checkbox"
          checked={recordar[c._id] !== false}
          onChange={e => setRecordar(prev => ({ ...prev, [c._id]: e.target.checked }))}
          style={{ accentColor: "#e30613", width: 15, height: 15 }}
        />
        recordar para este proveedor
      </label>
    </>
  );

  // ── Detalle (líneas del XML) ──
  const renderDetalle = (c) => {
    const d = detalles[c._id];
    if (!detalleAbierto[c._id]) return null;
    return (
      <div style={{ marginTop: 10, borderTop: "1px dashed #333", paddingTop: 8 }}>
        {!d || d.loading ? (
          <div style={{ fontSize: 11, color: "#8a8a8a" }}>Buscando el XML del documento…</div>
        ) : d.sinXml ? (
          <div style={{ fontSize: 11, color: "#8a8a8a" }}>
            Sin XML disponible en el archivo histórico — la clasificación va igual con los totales.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead>
                <tr>
                  {["Ítem", "Cant.", "P. unit", "Monto"].map((h, i) => (
                    <th key={h} style={{
                      textAlign: i === 0 ? "left" : "right", padding: "4px 8px",
                      color: "#999", fontSize: 10, textTransform: "uppercase",
                      letterSpacing: ".04em", borderBottom: "1px solid #2e2e2e", whiteSpace: "nowrap",
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(d.lineas || []).map((l, i) => (
                  <tr key={i}>
                    <td style={{ padding: "4px 8px", color: "#ddd", borderBottom: "1px solid #252525" }}>{l.nombre}</td>
                    <td style={{ padding: "4px 8px", color: "#bbb", borderBottom: "1px solid #252525", textAlign: "right", ...num, whiteSpace: "nowrap" }}>{l.qty ?? "—"}</td>
                    <td style={{ padding: "4px 8px", color: "#bbb", borderBottom: "1px solid #252525", textAlign: "right", ...num, whiteSpace: "nowrap" }}>{l.precio != null ? fmt(l.precio) : "—"}</td>
                    <td style={{ padding: "4px 8px", color: "#fff", fontWeight: 700, borderBottom: "1px solid #252525", textAlign: "right", ...num, whiteSpace: "nowrap" }}>{l.monto != null ? fmt(l.monto) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  if (error) return <div style={{ color: "#e74c3c", fontSize: 12, padding: 12 }}>Error: {error}</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>

      {/* ── Controles: mes + contador + ⚡ ── */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <select value={mes} onChange={e => setMes(e.target.value)}
          style={{ ...S.input, width: isMobile ? undefined : 130, flex: isMobile ? "2 1 110px" : undefined }}>
          {["01","02","03","04","05","06","07","08","09","10","11","12"].map(m => (
            <option key={m} value={m}>{new Date(2000, Number(m) - 1).toLocaleString("es-CL", { month: "long" })}</option>
          ))}
        </select>
        <select value={ano} onChange={e => setAno(e.target.value)}
          style={{ ...S.input, width: isMobile ? undefined : 80, flex: isMobile ? "1 1 80px" : undefined }}>
          {[2024, 2025, 2026].map(y => <option key={y}>{y}</option>)}
        </select>
        <span
          onClick={() => setVista("sin")}
          title="Ver las pendientes"
          className="t-02"
          style={{
            fontSize: 12, fontWeight: 700, padding: "5px 12px", borderRadius: 20, ...num,
            background: sinClasificar.length ? "#2d2000" : "#0d2a15",
            color: sinClasificar.length ? "#f39c12" : "#2ecc71",
            border: `1px solid ${sinClasificar.length ? "#4a3500" : "#0d4020"}`,
            whiteSpace: "nowrap", cursor: "pointer",
          }}>
          {loading ? "…" : `${sinClasificar.length} sin clasificar`}
        </span>
        <button type="button" onClick={aplicarReglas} disabled={aplicando || loading}
          className="t-02"
          style={{
            background: aplicando ? "#333" : "#e30613", color: "#fff", border: "none",
            borderRadius: isMobile ? 10 : 6, padding: "8px 16px", fontSize: 12, fontWeight: 700,
            cursor: aplicando ? "not-allowed" : "pointer", minHeight: 38,
            flex: isMobile ? "1 1 100%" : undefined,
          }}>
          {aplicando ? "Aplicando…" : "⚡ Aplicar reglas conocidas"}
        </button>
      </div>

      {msg && (
        <div style={{
          background: msg.startsWith("❌") ? "#2d1515" : "#0d2a15",
          border: `1px solid ${msg.startsWith("❌") ? "#e74c3c" : "#0d4020"}`,
          borderRadius: 8, padding: "8px 12px", fontSize: 12,
          color: msg.startsWith("❌") ? "#e74c3c" : "#2ecc71",
          display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
        }}>
          <span>{msg}</span>
          <button onClick={() => setMsg(null)} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: 14 }}>×</button>
        </div>
      )}

      {resumen && resumen.length > 0 && (
        <div style={{ ...S.card, overflowX: "auto" }}>
          <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 8 }}>Resumen de clasificación · últimos 6 meses</div>
          <table className="bini-table" style={{ width: "100%" }}><thead><tr><th>Período</th>{Object.values(TIPOS).map(t => <th key={t.label}>{t.label}</th>)}<th>Sin clasificar</th></tr></thead><tbody>{resumen.map(r => <tr key={r.periodo}><td>{r.periodo}</td><td>{fmt(r.empresa)}</td><td>{fmt(r.trabajo)}</td><td>{fmt(r.gasto_fijo)}</td><td>{fmt(r.personal)}</td><td>{fmt(r.sinClasificar)} · {r.nSinClasificar}</td></tr>)}</tbody></table>
        </div>
      )}

      {/* ── Filtro: pendientes / ya clasificadas ── */}
      <div className="chips-scroll" style={{ display: "flex", gap: 6, alignItems: "center" }}>
        {[["sin", `Sin clasificar (${sinClasificar.length})`], ["clasificadas", `Ya clasificadas (${clasificadas.length})`]].map(([k, l]) => (
          <button key={k} type="button" className="chip-touch no-touch-min t-02"
            onClick={() => setVista(k)} style={S.chip(vista === k)}>{l}</button>
        ))}
        <span style={{ fontSize: 10, color: "#8a8a8a", alignSelf: "center" }}>
          Con "recordar", el sistema aprende por proveedor
        </span>
      </div>

      {/* ── Tarjetas ── */}
      {loading ? (
        <div style={{ ...S.card, textAlign: "center", color: "#8a8a8a", fontSize: 12 }}>Cargando compras de {periodo}…</div>
      ) : visibles.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", color: "#8a8a8a", fontSize: 12 }}>
          {vista === "sin"
            ? (compras.length ? "Todo clasificado este mes ✓" : `Sin compras para ${periodo}.`)
            : "Nada clasificado aún este mes."}
        </div>
      ) : visibles.map(c => {
        const clas = c.clasificacion?.tipo ? TIPOS[c.clasificacion.tipo] : null;
        const sug = c.sugerencia ? TIPOS[c.sugerencia] : null;
        return (
          <div key={c._id}
            style={{
              ...S.card,
              opacity: fading.has(c._id) ? 0 : 1,
              transform: fading.has(c._id) ? "translateX(12px)" : "none",
              transition: "opacity .35s ease, transform .35s ease",
            }}>

            {/* cabecera de la tarjeta */}
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {c.razonEmisor || c.rutEmisor || "—"}
                </div>
                <div style={{ fontSize: 11, color: "#8a8a8a", ...num }}>
                  {c.tipoDoc === 61 ? "NC" : "F°"}{c.folio} · {fmtD(c.fecha)} · {c.rutEmisor || "—"}
                </div>
              </div>
              <div style={{ fontSize: isMobile ? 18 : 20, fontWeight: 800, color: c.tipoDoc === 61 ? "#e74c3c" : "#fff", ...num, whiteSpace: "nowrap" }}>
                {c.tipoDoc === 61 ? "−" : ""}{fmt(c.montoTotal)}
              </div>
            </div>

            {/* chips: sugerencia (pendientes) / clasificación actual */}
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginTop: 6 }}>
              {!clas && sug && (
                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 9px", borderRadius: 20, background: sug.bg, color: sug.color, border: `1px solid ${sug.color}` }}>
                  sugerido: {sug.emoji} {sug.label} (regla)
                </span>
              )}
              {clas && (
                <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 20, background: clas.bg, color: clas.color, border: `1px solid ${clas.border}` }}>
                  {clas.emoji} {clas.label}
                  {c.clasificacion.workOrder ? ` · OT N°${c.clasificacion.workOrder.number ?? ""}` : ""}
                  {c.clasificacion.auto ? " · auto" : ""}
                </span>
              )}
              {clas && (
                <span style={{ fontSize: 10, color: "#8a8a8a" }}>
                  {c.clasificacion.por ? `por ${c.clasificacion.por.split("(").pop().replace(")", "")}` : ""} {c.clasificacion.fecha ? `· ${fmtD(c.clasificacion.fecha)}` : ""}
                </span>
              )}
              <button type="button" onClick={() => verDetalle(c)}
                style={{ background: "none", border: "none", color: "#3498db", fontSize: 11, cursor: "pointer", padding: "2px 4px", marginLeft: "auto" }}>
                ver detalle {detalleAbierto[c._id] ? "▴" : "▾"}
              </button>
              {clas && !editando[c._id] && (
                <button type="button"
                  onClick={() => { setEditando(prev => ({ ...prev, [c._id]: true })); setOtAbierto(null); }}
                  style={{ background: "#2a2a2a", border: "1px solid #3a3a3a", color: "#cbd5e1", borderRadius: 6, padding: "3px 10px", fontSize: 11, cursor: "pointer" }}>
                  re-clasificar
                </button>
              )}
            </div>

            {renderDetalle(c)}

            {/* botones grandes (pendientes siempre; clasificadas solo al re-clasificar) */}
            {(!clas || editando[c._id]) && (
              <div style={{ marginTop: 10 }}>
                {renderBotones(c)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ClasificarCompras;
